import { useState } from "react";
import { useNotify, useUpdate } from "ra-core";
import { Pencil } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

import type { ZoomMeetingSummaryView } from "../types";
import { isGenericTopic } from "./meetingDisplayTitle";

/**
 * A meeting's name in the list, renamable in place: a pencil shows on hover,
 * and the name becomes an input. Enter or Save stores it in custom_title, Escape
 * or Cancel leaves it. Clearing it restores Zoom's topic, as on the meeting page
 * (MeetingTitle, LCCRM-88).
 *
 * The row opens the meeting on click, so nothing in here may reach it.
 */
export const MeetingNameCell = ({
  record,
}: {
  record: ZoomMeetingSummaryView;
}) => {
  const notify = useNotify();
  const [update] = useUpdate();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);

  const topic = (record.meeting_topic ?? "").trim();
  // A name given in Atomic is a real name, so it is never muted (LCCRM-88).
  const custom = (record.custom_title ?? "").trim();
  const shown = custom || topic;

  const start = () => {
    setDraft(shown);
    setEditing(true);
  };

  const submit = async () => {
    const next = draft.trim() || null;
    // Accepting Zoom's topic unchanged is not a rename.
    const unchanged =
      custom === "" ? next === topic || next == null : next === custom;
    if (unchanged) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await update(
        "zoom_meetings",
        {
          id: record.id,
          data: { custom_title: next },
          previousData: { id: record.id },
        },
        { returnPromise: true },
      );
      setEditing(false);
    } catch {
      notify("Couldn't rename the meeting.", { type: "error" });
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <div
        className="flex items-center gap-1 min-w-0"
        onClick={(event) => event.stopPropagation()}
      >
        <Input
          autoFocus
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Escape") setEditing(false);
            if (event.key === "Enter") {
              event.preventDefault();
              void submit();
            }
          }}
          aria-label="Meeting name"
          placeholder="Leave empty to use Zoom's name"
          className="h-7 min-w-0 text-sm"
          disabled={saving}
        />
        <Button
          type="button"
          size="sm"
          className="h-7"
          onClick={submit}
          disabled={saving}
        >
          Save
        </Button>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="h-7"
          onClick={() => setEditing(false)}
          disabled={saving}
        >
          Cancel
        </Button>
      </div>
    );
  }

  return (
    <span className="group/name flex min-w-0 items-center gap-1">
      <span
        title={shown || undefined}
        className={cn(
          "block truncate",
          !custom && isGenericTopic(topic) && "italic text-muted-foreground",
        )}
      >
        {shown || "Zoom Meeting"}
      </span>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        // Hidden until the name is hovered, but still reachable by keyboard.
        className="size-6 shrink-0 text-muted-foreground opacity-0 group-hover/name:opacity-100 focus-visible:opacity-100"
        onClick={(event) => {
          event.stopPropagation();
          start();
        }}
        aria-label="Rename meeting"
        title="Rename meeting"
      >
        <Pencil className="size-3.5" />
      </Button>
    </span>
  );
};
